<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import axios from 'axios';
import Swal from 'sweetalert2';


axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post['Content-Type'] = 'application/json;charset=utf-8';
axios.defaults.headers.common['api-key'] = process.env.VUE_APP_API_KEY;


const store = useStore();
const user = computed(() => store.getters.getUserDetails);

const userRole = computed(() => {
  const attributes = user.value;
  if (!Array.isArray(attributes)) return null;
  const roleAttr = attributes.find(attr => attr.Name === 'custom:role');
  return roleAttr ? roleAttr.Value : null;
});


const selectedType = ref(null);
const file = ref(null);
const uploading = ref(false);
const progress = ref(0);


const uploadTypes = [
  {
    key: 'category',
    title: 'Categories',
    icon: 'fa-tags',
    description: 'Upload item categories and their details.',
  },
  {
    key: 'items',
    title: 'Items',
    icon: 'fa-boxes',
    description: 'Upload product or inventory item details.',
  },
  {
    key: 'departments',
    title: 'Departments',
    icon: 'fa-building',
    description: 'Upload department information such as name and contact.',
  },
];

const handleFileChange = (event) => {
  file.value = event.target.files[0];
};

const downloadTemplate = async () => {
  if (!selectedType.value) {
    Swal.fire('Please choose what you want to upload first!');
    return;
  }

  try {
    const response = await axios.get(`/v1/inserts/template/${selectedType.value}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `${selectedType.value}_template.xlsx`;
    link.click();
  } catch (error) {
    Swal.fire('Error', 'Failed to download the template.', 'error');
  }
};

const uploadFile = async () => {
  if (!selectedType.value || !file.value) {
    Swal.fire('Please select what you want to upload and attach a file!');
    return;
  }

  const formData = new FormData();
  formData.append('file', file.value);

  try {
    uploading.value = true;
    progress.value = 0;

    const response = await axios.post(`/v1/inserts/upload/${selectedType.value}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (event.lengthComputable) {
          progress.value = Math.round((event.loaded * 100) / event.total);
        }
      },
    });

    Swal.fire({
      icon: 'success',
      title: 'Upload Complete!',
      text: response.data.message || `File uploaded successfully for ${selectedType.value}`,
      confirmButtonColor: '#198754',
    });
  } catch (error) {
    Swal.fire({
      icon: 'error',
      title: 'Upload Failed',
      html: `<div style="text-align:left">${error.response?.data?.detail || 'Unexpected error occurred.'}</div>`,
      confirmButtonColor: '#dc3545',
    });
  } finally {
    uploading.value = false;
    progress.value = 0;
    file.value = null;
  }
};
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>

    <div class="page-wrapper">
      <div class="content upload-page">
        <pageaddheader
          :title="'UPLOAD DATA'"
          :title1="'Import Categories, Items or Departments using Excel templates'"
        />

        <!-- Step 1: Choose Upload Type -->
        <div class="card shadow-sm mb-4">
          <div class="card-body p-4">
            <h6 class="fw-bold mb-3 text-primary">Step 1 — What would you like to upload?</h6>
            <div class="row g-3">
              <div
                v-for="type in uploadTypes"
                :key="type.key"
                class="col-md-4"
              >
                <div
                  class="upload-option p-3 text-center h-100"
                  :class="{ active: selectedType === type.key }"
                  @click="selectedType = type.key"
                >
                  <i :class="`fa ${type.icon} fa-2x mb-2`"></i>
                  <h6 class="fw-semibold mb-1">{{ type.title }}</h6>
                  <p class="text-muted small">{{ type.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Download Template -->
        <div class="card shadow-sm mb-4">
          <div class="card-body p-4">
            <h6 class="fw-bold mb-3 text-primary">Step 2 — Download Excel Template</h6>
            <p class="text-muted mb-3">
              Download the Excel template for your chosen data type, fill it out, and save it.
            </p>
            <button
              class="btn btn-outline-primary rounded-pill px-4 py-2"
              @click="downloadTemplate"
              :disabled="!selectedType"
            >
              <i class="fa fa-download me-2"></i>
              {{ selectedType ? `Download ${selectedType.charAt(0).toUpperCase() + selectedType.slice(1)} Template` : 'Download Template' }}
            </button>
          </div>
        </div>

        <!-- Step 3: Upload Excel File -->
        <div class="card shadow-sm mb-4 text-center">
          <div class="card-body p-4">
            <h6 class="fw-bold mb-3 text-primary">Step 3 — Upload Your Excel File</h6>
            <input
              type="file"
              accept=".xlsx,.xls"
              id="fileInput"
              class="d-none"
              @change="handleFileChange"
            />
            <label for="fileInput" class="upload-label">
              <i class="fa fa-file-excel me-2"></i>
              {{ file ? file.name : 'Choose Excel File' }}
            </label>

            <div v-if="file" class="mt-3 text-muted small">
              <i class="fa fa-check-circle text-success me-1"></i>
              File ready to upload
            </div>
          </div>
        </div>

        <!-- Step 4: Upload -->
        <div class="card shadow-sm">
          <div class="card-body p-4 text-center">
            <h6 class="fw-bold mb-3 text-primary">Step 4 — Upload Data</h6>
            <button
              class="btn btn-success rounded-pill px-5 py-2"
              :disabled="!file || uploading"
              @click="uploadFile"
            >
              <i class="fa fa-upload me-2"></i>
              {{ uploading ? 'Uploading...' : 'Start Upload' }}
            </button>

            <div v-if="uploading" class="mt-4">
              <div class="progress rounded-pill" style="height: 20px;">
                <div
                  class="progress-bar progress-bar-striped progress-bar-animated bg-success fw-semibold"
                  role="progressbar"
                  :style="{ width: progress + '%' }"
                >
                  {{ progress }}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Info Section -->
        <div class="info-box mt-5 p-4 rounded shadow-sm">
          <h6 class="fw-semibold mb-2 text-primary">
            <i class="fa fa-info-circle me-2"></i> Helpful Tips
          </h6>
          <ul class="mb-0 text-muted">
            <li>Always use the downloaded Excel template before uploading.</li>
            <li>Ensure all required columns (highlighted in the template) are filled.</li>
            <li>Supported types: Categories, Items, Departments.</li>
            <li>If an error occurs, check which row the message refers to.</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.upload-page {
  max-width: 900px;
  margin: auto;
}
.upload-option {
  border: 2px solid #e9ecef;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #fafafa;
}
.upload-option:hover {
  border-color: #007bff;
  background-color: #f1f7ff;
}
.upload-option.active {
  border-color: #007bff;
  background-color: #e7f1ff;
}
.upload-label {
  display: inline-block;
  padding: 14px 28px;
  border: 2px dashed #cfd9e4;
  border-radius: 12px;
  background: #f9fbfd;
  cursor: pointer;
  color: #333;
  transition: all 0.3s ease;
}
.upload-label:hover {
  border-color: #007bff;
  background-color: #f1f7ff;
  color: #007bff;
}
.info-box {
  background-color: #f8faff;
  border: 1px solid #e6ecf1;
}
.progress-bar {
  transition: width 0.3s ease;
}
</style>
